import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useApolloClient } from '@apollo/client';
import AttendeePdfAccessLayout from './AttendeePdfAccessLayout';
import { BodySmall, H4 } from '@styled';
import { ATTENDEE_PDF_URL_QUERY } from '@general/graphql/event.graph';
import colors from '@general/colors';
import { mediaQueries } from '@general/breakpoints';
import { PdfCardContainer, PdfCardBody, PrimaryAccentHr, PdfLinkButton } from './PdfCard';

const Backdrop = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    z-index: 99998;
`;

const ContainerStyled = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 99999;
    width: 90%;

    @media ${mediaQueries.md} {
        width: 80%;
        max-width: 800px;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 16px;
    color: ${colors.darkGray};
`;

const PdfGroupsBody = styled(PdfCardBody)`
    max-height: 70vh;
    overflow-y: auto;

    @media ${mediaQueries.md} {
        padding-bottom: 56px;
    }
`;

const GroupSection = styled.div`
    margin-top: 1.5rem;
`;

const PdfLinkList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0.5rem 0 0 0;
`;

const PdfLinkItem = styled.li`
    margin-top: 0.5rem;
`;

const AttendeePdfAccessModal = ({ onClose, eventCode, pdfGroups }) => {
    const [acknowledged, setAcknowledged] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [activeFetchLabel, setActiveFetchLabel] = useState(null);
    const [fetchError, setFetchError] = useState(null);
    const apolloClient = useApolloClient();
    const isSplit = pdfGroups.some(g => g.heading !== null);

    const fetchAndOpenPdf = async label => {
        // Open the tab synchronously (before any await) so the browser treats it
        // as user-initiated. window.open after an await is blocked as a popup.
        const newTab = window.open('', '_blank');
        setIsFetching(true);
        setActiveFetchLabel(label ?? null);
        setFetchError(null);
        let success = false;
        try {
            const { data } = await apolloClient.query({
                query: ATTENDEE_PDF_URL_QUERY,
                variables: { codes: [eventCode], label: label ?? null },
                fetchPolicy: 'network-only',
            });
            const url = data?.events?.[0]?.attendeePdfUrl;
            if (url) {
                if (newTab && !newTab.closed) {
                    newTab.location.assign(url);
                    success = true;
                } else {
                    setFetchError('Your browser blocked the download popup. Please allow popups for this site and try again.');
                }
            } else {
                newTab?.close();
                setFetchError('The attendee list is not available. Please try again later.');
            }
        } catch {
            newTab?.close();
            setFetchError('Something went wrong. Please try again.');
        } finally {
            setIsFetching(false);
            if (!isSplit && success) {
                onClose();
            }
        }
    };

    const handleAcknowledge = () => {
        if (isSplit) {
            setAcknowledged(true);
        } else {
            fetchAndOpenPdf(null);
        }
    };

    return (
        <>
            <Backdrop onClick={onClose} />
            <ContainerStyled>
                <CloseButton aria-label="Close" onClick={onClose}>Close  X</CloseButton>

                {!acknowledged ? (
                    <AttendeePdfAccessLayout
                        onAccept={handleAcknowledge}
                        loading={isFetching}
                        error={fetchError}
                    />
                ) : (
                    <PdfCardContainer>
                        <PrimaryAccentHr />
                        <PdfGroupsBody>
                            <H4>Attendee Lists</H4>

                            {fetchError && (
                                <BodySmall className="mt-2" style={{ color: '#991b1b' }}>
                                    {fetchError}
                                </BodySmall>
                            )}

                            <div className="mt-2 p-2 bg-bright-sun-tint text-black d-flex align-items-top">
                                <div>&#9432;</div>
                                <div className="d-flex align-items-center ml-2">
                                    <BodySmall className="mb-0">
                                        Please note attendee lists are the proprietary property of ICSC
                                        and any reproduction, distribution, or sales of these lists are
                                        prohibited.
                                    </BodySmall>
                                </div>
                            </div>

                            {pdfGroups.map((group, groupIndex) => (
                                <GroupSection key={group.heading ?? groupIndex}>
                                    {group.heading && <H4>{group.heading}</H4>}
                                    <PdfLinkList>
                                        {group.pdfs.map((pdf, pdfIndex) => (
                                            <PdfLinkItem key={pdf.label ?? pdfIndex}>
                                                <PdfLinkButton
                                                    onClick={() => fetchAndOpenPdf(pdf.label)}
                                                    disabled={isFetching}
                                                >
                                                    {isFetching && activeFetchLabel === pdf.label
                                                        ? 'Opening...'
                                                        : (pdf.displayLabel || pdf.label || 'Download Attendee List')}
                                                </PdfLinkButton>
                                            </PdfLinkItem>
                                        ))}
                                    </PdfLinkList>
                                </GroupSection>
                            ))}
                        </PdfGroupsBody>
                    </PdfCardContainer>
                )}
            </ContainerStyled>
        </>
    );
};

AttendeePdfAccessModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    eventCode: PropTypes.string.isRequired,
    pdfGroups: PropTypes.arrayOf(
        PropTypes.shape({
            heading: PropTypes.string,
            pdfs: PropTypes.arrayOf(
                PropTypes.shape({
                    label: PropTypes.string,
                    displayLabel: PropTypes.string,
                })
            ).isRequired,
        })
    ).isRequired,
};

export default AttendeePdfAccessModal;
